import { translate } from "app/filters/i18nFilter";
import {
    LocationAssetType,
    type LocationChipItem,
} from "app/pages/sales-intelligence/common-components/dropdown/LocationDropdown/types";
import type { ChipStatus } from "app/pages/sales-intelligence/common-components/filters-panel/types/chip";
import { FilterChipVariant } from "app/pages/sales-intelligence/common-components/filters-panel/types/commonEnums";
import type { ICountryObject } from "app/services/CountryService";
import CountryService from "app/services/CountryService";
import type { IRegionObject } from "app/services/RegionService";

import { buildLocationGroups, collapseSelectedLocationCodes, getSelectedCodes } from "./location";

type BuildLocationChipsParams = {
    value: Parameters<typeof getSelectedCodes>[0];
    regions: readonly IRegionObject[];
    countries: readonly ICountryObject[];
    otherCountriesLabel?: string;
    excludeCountryIds?: ReadonlySet<number>;
    onGetLifecycleStatus?: (value: string | number) => ChipStatus | undefined;
};

const getRegionChipStatus = (
    regionName: string,
    memberCountryIds: readonly number[],
    onGetLifecycleStatus?: (value: string | number) => ChipStatus | undefined,
): ChipStatus | undefined => {
    if (onGetLifecycleStatus?.(regionName) === "removed") {
        return "removed";
    }

    if (memberCountryIds.length > 0 && memberCountryIds.every((id) => onGetLifecycleStatus?.(id) === "removed")) {
        return "removed";
    }

    return undefined;
};

export const buildLocationChips = ({
    value,
    regions,
    countries,
    otherCountriesLabel = translate("si.lead_gen_filters.countries.other_countries_region"),
    excludeCountryIds,
    onGetLifecycleStatus,
}: BuildLocationChipsParams): LocationChipItem[] => {
    const selectedCodes = getSelectedCodes(value, regions);
    if (selectedCodes.size === 0) {
        return [];
    }

    const filteredSelectedCodes = excludeCountryIds
        ? new Set([...selectedCodes].filter((id) => !excludeCountryIds.has(id)))
        : selectedCodes;

    if (filteredSelectedCodes.size === 0) {
        return [];
    }

    const { groups } = buildLocationGroups(regions, countries, otherCountriesLabel);
    const collapsed = collapseSelectedLocationCodes(filteredSelectedCodes, groups);

    const regionChips: LocationChipItem[] = collapsed.regions.map(({ region, memberCountryIds }) => {
        const regionLabel = region.text ?? region.name;
        const tooltipLines = memberCountryIds
            .map((id) => CountryService.getCountryById(id)?.text)
            .filter((text): text is string => Boolean(text));

        return {
            id: region.name,
            type: LocationAssetType.Region,
            variant: FilterChipVariant.Icon,
            iconName: "location-circled",
            text: `${regionLabel} (${memberCountryIds.length})`,
            countryIds: memberCountryIds,
            tooltipLines,
            chipStatus: getRegionChipStatus(region.name, memberCountryIds, onGetLifecycleStatus),
        };
    });

    const countryChips: LocationChipItem[] = collapsed.countryIds.map((code) => {
        const country = CountryService.getCountryById(code);
        const assetType = country?.parent ? LocationAssetType.State : LocationAssetType.Country;

        return {
            id: String(country?.id ?? code),
            variant: country?.parent ? FilterChipVariant.State : FilterChipVariant.Country,
            text: country?.text ?? String(code),
            ...(country?.code ? { code: country.code } : {}),
            type: assetType,
            chipStatus: onGetLifecycleStatus?.(code),
        };
    });

    return [...regionChips, ...countryChips];
};

/**
 * Codes left after the user removes a location chip. A region chip drops every member country it
 * stands for; any other chip drops only its own code.
 */
export const getCodesAfterLocationChipRemoval = (
    item: LocationChipItem,
    chipId: string,
    selectedCodes: Iterable<number>,
): number[] => {
    if (item.type === LocationAssetType.Region) {
        const idsToRemove = new Set(item.countryIds);
        return [...selectedCodes].filter((code) => !idsToRemove.has(code));
    }

    const removedCode = Number(chipId);
    return [...selectedCodes].filter((code) => code !== removedCode);
};
